import React, {useContext, useEffect, useState} from 'react';
import {NavLink, withRouter} from 'react-router-dom';

import {UserContext} from 'context/UserContext';

import UserMenu from 'components/Page/Header/UserMenu/UserMenu';
import WelcomeLogo from 'components/common/icons/redesign/WelcomeLogo';

import Routes from 'constants/Routes';
import {leftOverRightRoutes, rightOverLeftRoutes, registerRoutes} from 'components/Page/Page';
import {isExternal, isExternalModerator, isExternalTranslator, isObserver} from 'constants/UserRoles';

import style from './Header.module.scss';
import cn from 'classnames';
import {useTranslation} from 'react-i18next';

export const LogoStyles = {
    light: {
        icon: '#fff',
        text: '#fff',
    },
    colored: {
        icon: '',
        text: '#1c1b1f',
    },
};

const signLinks = [
    {
        label: 'pages.auth.login',
        to: Routes.signIn.path,
    },
    {
        label: 'pages.auth.signUp',
        to: Routes.signUp.path,
    },
];

function Header({history, match, headerContent, browseHeader, withLinks}) {
    const {user, logout, previousRoute} = useContext(UserContext);
    const {t} = useTranslation();
    const {styleToMount, styleToUnmount} = resolveLogoStyle(match, previousRoute);
    const [logoStyle, setLogoStyle] = useState(styleToUnmount);

    useEffect(() => {
        setTimeout(() => setLogoStyle(styleToMount), 0);
    }, []);

    const headerWrapper = cn(style.header, {[style.browseHeader]: browseHeader});

    return (
        <div className={headerWrapper}>
            <div className={style.leftAligned}>
                <WelcomeLogo
                    onClick={() => history.push(Routes.home.path)}
                    className={style.logo}
                    fillIcon={logoStyle.icon}
                    fillText={logoStyle.text}
                />
                {headerContent}
            </div>
            {withLinks && (
                <div className={style.navigation}>
                    {signLinks.map(link => (
                        <NavLink key={link.to} to={link.to} className={style.signLink}>
                            <span>{t(link.label)}</span>
                        </NavLink>
                    ))}
                </div>
            )}
            {user &&
                !isExternal(user.role) &&
                !isExternalModerator(user.role) &&
                !isExternalTranslator(user.role) &&
                !isObserver(user.role) && <UserMenu logout={logout} />}
        </div>
    );
}

const resolveLogoStyle = (match, previousRoute) => {
    if (!previousRoute.route) {
        return getInitialLogoStyles(match.path);
    }

    if (match.path && match.path.includes(Routes.signUpTeam.path)) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.colored};
    }

    if (leftOverRightRoutes.includes(match.path) && rightOverLeftRoutes.includes(previousRoute.route)) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (leftOverRightRoutes.includes(previousRoute.route) && rightOverLeftRoutes.includes(match.path)) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (match.path === Routes.confirmRegistration.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (previousRoute.route === `${Routes.signUp.path}/:role` && match.path === Routes.signIn.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (match.path === Routes.browseInterviews.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (match.path === Routes.signIn.path && previousRoute.route === Routes.welcome.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (match.path === Routes.signIn.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    if (match.path.includes(Routes.interviewDetail.category)) {
        return getInitialLogoStyles(match.path);
    }

    if (!registerRoutes.includes(previousRoute.route) && registerRoutes.includes(match.path)) {
        return getInitialLogoStyles(match.path);
    }

    if (match.path === Routes.welcome.path) {
        return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
    }

    return {styleToMount: LogoStyles.colored, styleToUnmount: LogoStyles.light};
};

const getInitialLogoStyles = path => {
    switch (path) {
        case Routes.signIn.path:
        case `${Routes.signUp.path}/:role`:
        case Routes.confirmRegistration.path:
        case Routes.welcome.path:
            return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
        case Routes.interviewDetail.path:
            return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
        case Routes.browseInterviews.path:
            return {styleToMount: LogoStyles.light, styleToUnmount: LogoStyles.light};
        case Routes.signUp.path:
        case Routes.resetPassword.path:
            return {styleToMount: LogoStyles.colored, styleToUnmount: LogoStyles.light};
        default:
            return {styleToMount: LogoStyles.colored, styleToUnmount: LogoStyles.light};
    }
};

export default withRouter(Header);
